import React, { useEffect, useState } from "react";
import { auth, db, storage } from "../FirebaseConfig";
import { addDoc, collection } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Footer from "./Footer";
import Header from "./Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { parseISO, addWeeks, format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { uploadFileToCloudinary } from "../utils/cloudinary";

const MentorForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    coursename: "",
    discription: "",
    duration: "",
    charges: "",
    starting: "",
    picture: null,
    portfolio: "",
    linkedin: "",
    // website: "",
    phone: "",
    email: "",
    lectureMode: "",
    lectureTime: "",
    weekdays: [],
  });

  const coursenameOptions = [
    "3D Art",
    "AI",
    "App Development",
    "Artificial Intelligence",
    "Blockchain",
    "Branding",
    "Business Development",
    "Business Intelligence",
    "Cloud Computing",
    "Coding & Programming",
    "Communication & Presentation",
    "Computer Science",
    "Content Marketing",
    "Content Writing",
    "Cybersecurity",
    "Data Science",
    "Digital Marketing",
    "Email Marketing",
    "Full Stack Web Development",
    "Fundamentals of IT",
    "Google Data Analytics",
    "Graphic Design",
    "Lead Generation",
    "Machine Learning",
    "Meta Ads",
    "Project Management",
    "Python",
    "Quality Assurance",
    "Sales & Marketing",
    "SEO",
    "Tecnocracy",
    "Videography",
    "Web Development",
    "Wordpress",
    "YouTube Automation",
  ];

  const durationOptions = [
    "1 Week",
    "2 Weeks",
    "3 Weeks",
    "4 Weeks",
    "5 Weeks",
    "6 Weeks",
    "7 Weeks",
    "8 Weeks",
    "9 Weeks",
    "10 Weeks",
    "11 Weeks",
    "12 Weeks",
  ];

  const chargeOptions = [
    "5000 PKR",
    "10,000 PKR",
    "15,000 PKR",
    "20,000 PKR",
    "25,000 PKR",
    "30,000 PKR",
    "35,000 PKR",
    "40,000 PKR",
    "45,000 PKR",
    "50,000 PKR",
  ];

  useEffect(() => {
    if (!auth.currentUser) {
      toast.error("Please log in to access this page.");
      navigate("/mentor-login");
    } else {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user.role !== "mentor") {
        toast.error("Unauthorized access. Please log in as a mentor.");
        navigate("/mentor-login");
      }
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
      }));
    }
  }, [navigate]);

  const handleFileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.files[0] });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const uploadFileToStorage = async (file, path) => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const pictureDownloadUrl = await uploadFileToCloudinary(formData.picture);

      // Parse starting date (in yyyy-MM-dd format)
      const parsedStartDate = parseISO(formData.starting);

      // Extract weeks from duration string
      const weeks = parseInt(formData.duration.split(" ")[0]);

      // Calculate ending date
      const endingDate = format(addWeeks(parsedStartDate, weeks), "yyyy-MM-dd");

      const convertTo12HourFormat = (time24) => {
        const [hourStr, minute] = time24.split(":");
        let hour = parseInt(hourStr, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        hour = hour % 12 || 12;
        return `${hour}:${minute} ${ampm}`;
      };

      const formattedLectureTime = convertTo12HourFormat(formData.lectureTime);

      // Add document to Firestore
      await addDoc(collection(db, "courses"), {
        uid: auth.currentUser.uid,
        name: formData.name,
        coursename: formData.coursename,
        discription: formData.discription,
        duration: formData.duration,
        charges: formData.charges,
        starting: formData.starting,
        endingdate: endingDate,
        pictureUrl: pictureDownloadUrl,
        portfolio: formData.portfolio,
        linkedin: formData.linkedin,
        // website: formData.website,
        phone: formData.phone,
        email: formData.email,
        lectureMode: formData.lectureMode,
        lectureTime: formattedLectureTime,
        weekdays: formData.weekdays,
        status: "pending",
      });

      toast.success(
        "Your request has been submitted. You will recieve a confirmation email soon. Thank you!"
      );
      setFormData({
        name: "",
        coursename: "",
        discription: "",
        duration: "",
        charges: "",
        starting: "",
        picture: null,
      });
      navigate("/");
    } catch (error) {
      toast.error(`Error uploading data: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="max-w-4xl md:mx-auto mx-3 p-6 md:my-12 md:mb-12 mb-32 my-3 bg-white shadow-lg rounded-lg">
        <div className="md:mb-6 mb-3">
          <h2 className="text-2xl font-semibold text-gray-800">
            Submit Your Information (As a Mentor)
          </h2>
          <div className="border-t-4 border-orange-500 mt-2 w-10 rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-gray-600">Your Name:</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-gray-600">Course Name:</label>
            <select
              name="coursename"
              value={formData.coursename}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            >
              <option value="">Select Course</option>
              {coursenameOptions.map((coursename, index) => (
                <option key={index} value={coursename}>
                  {coursename}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-600">Enrollment Charges:</label>
            <select
              name="charges"
              value={formData.charges}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            >
              <option value="">Select Charges</option>
              {chargeOptions.map((charge, index) => (
                <option key={index} value={charge}>
                  {charge}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-600">Duration:</label>
            <select
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            >
              <option value="">Select Duration</option>
              {durationOptions.map((duration, index) => (
                <option key={index} value={duration}>
                  {duration}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-600">Portfolio (URL):</label>
            <input
              type="url"
              name="portfolio"
              value={formData.portfolio || ""}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="https://yourportfolio.com"
              required
            />
          </div>

          <div>
            <label className="block text-gray-600">LinkedIn:</label>
            <input
              type="url"
              name="linkedin"
              value={formData.linkedin || ""}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="https://linkedin.com/in/yourprofile"
              required
            />
          </div>

          {/* <div>
            <label className="block text-gray-600">Website:</label>
            <input
              type="url"
              name="website"
              value={formData.website || ""}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="https://yourwebsite.com"
              required
            />
          </div> */}

          <div>
            <label className="block text-gray-600">Phone Number:</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone || ""}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="+92XXXXXXXXXX"
              pattern="^\+?\d{10,15}$"
              required
            />
          </div>

          <div>
            <label className="block text-gray-600">Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-gray-600">Lecture Mode:</label>
            <select
              name="lectureMode"
              value={formData.lectureMode || ""}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            >
              <option value="">Select Mode</option>
              <option value="Zoom">Zoom</option>
              <option value="Google Meet">Google Meet</option>
              <option value="Physical">Physical</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-600">Lecture Time:</label>
            <input
              type="time"
              name="lectureTime"
              value={formData.lectureTime || ""}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-gray-600">Select Week Days:</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {[
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ].map((day) => (
                <label key={day} className="flex items-center">
                  <input
                    type="checkbox"
                    name="weekdays"
                    value={day}
                    checked={formData.weekdays?.includes(day) || false}
                    onChange={(e) => {
                      const selected = formData.weekdays || [];
                      const updated = e.target.checked
                        ? [...selected, day]
                        : selected.filter((d) => d !== day);
                      setFormData({ ...formData, weekdays: updated });
                    }}
                    className="mr-2"
                  />
                  {day}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-gray-600">Starting Date:</label>
            <input
              type="date"
              name="starting"
              value={formData.starting}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
              min={new Date().toISOString().split("T")[0]}
            />
            <p className="text-sm text-gray-500 mt-1">
              Please select a valid future start date.
            </p>
          </div>

          <div>
            <label className="block text-gray-600">Course Description:</label>
            <textarea
              name="discription"
              value={formData.discription}
              onChange={handleChange}
              rows={5}
              placeholder="Describe its usefulness. For whom it is good to join? Career Development Pathway, Scope & everything in detail"
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-gray-600">
              Thumbnail / Course Related Picture:
            </label>
            <input
              type="file"
              name="picture"
              onChange={handleFileChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-6 font-semibold shadow-lg ${
              isSubmitting
                ? "bg-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-orange-500 to-red-500 text-white"
            }`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
      <ToastContainer
  position="top-right"
  autoClose={4000}
  hideProgressBar={false}
  newestOnTop={false}
  closeOnClick
  rtl={false}
  pauseOnFocusLoss
  draggable
  pauseOnHover
  theme="light"
/>

      <Footer />
    </>
  );
};

export default MentorForm;
